import { useState, useMemo } from 'react';
import { adminApi } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Loading, ApiError, EmptyState } from '../components/ui/Loading';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { useToast } from '../context/ToastContext';
import { formatDate, formatPrice } from '../utils/helpers';

const DEFAULT_FORM = {
  code: '', description: '', discount_type: 'percentage', discount_value: '',
  points_required: '', target_type: 'all', target_user_ids: '',
  valid_from: '', valid_until: '', max_uses: '', max_uses_per_user: '1',
  min_order_amount: '0', is_active: true,
};

const DEFAULT_LOYAL = {
  min_points: '500', discount_type: 'percentage',
  discount_value: '10', duration_days: '30', min_order_amount: '0',
};

const DiscountValue = ({ d }) => {
  if (d.discount_type === 'percentage')
    return <span style={{ color: 'var(--accent)', fontWeight: 700 }}>−{d.discount_value}%</span>;
  if (d.discount_type === 'fixed')
    return <span style={{ color: 'var(--green)', fontWeight: 700 }}>−{formatPrice(d.discount_value)}</span>;
  return <span style={{ color: 'var(--purple)', fontWeight: 700 }}>⭐ {d.discount_value} pts requis</span>;
};

const TARGET_LABELS = {
  all:            'Tous',
  category_A:     'Cat. A',
  category_B:     'Cat. B',
  specific_users: 'Utilisateurs',
};
const TARGET_CLS = {
  all:            'badge-gray',
  category_A:     'badge-green',
  category_B:     'badge-blue',
  specific_users: 'badge-purple',
};

export const Promos = () => {
  const toast = useToast();

  const { data: raw, loading, error, refetch, setData } = useApi(
    () => adminApi.getDiscountsList()
  );

  const [search,       setSearch]       = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [createModal,  setCreateModal]  = useState(false);
  const [loyalModal,   setLoyalModal]   = useState(false);
  const [form,         setForm]         = useState(DEFAULT_FORM);
  const [loyalForm,    setLoyalForm]    = useState(DEFAULT_LOYAL);
  const [saving,       setSaving]       = useState(false);
  const [detailModal,  setDetailModal]  = useState(null);

  const discounts = raw?.data || [];

  const filtered = useMemo(() => discounts.filter(d => {
    const q = search.toLowerCase();
    const matchSearch = !q || d.code?.toLowerCase().includes(q) || d.description?.toLowerCase().includes(q);
    const matchActive = activeFilter === 'all'
      || (activeFilter === 'active' ? d.is_active : !d.is_active);
    return matchSearch && matchActive;
  }), [discounts, search, activeFilter]);

  const handleCreate = async () => {
    if (!form.code.trim() || !form.discount_value) {
      toast.error('Le code et la valeur de remise sont obligatoires.');
      return;
    }
    setSaving(true);
    try {
      const res = await adminApi.createDiscount({
        code:               form.code.trim().toUpperCase(),
        description:        form.description,
        discount_type:      form.discount_type,
        discount_value:     Number(form.discount_value),
        points_required:    form.discount_type === 'points' ? Number(form.points_required) : null,
        target_type:        form.target_type,
        target_user_ids:    form.target_type === 'specific_users'
                              ? form.target_user_ids.split(',').map(Number).filter(Boolean)
                              : null,
        valid_from:         form.valid_from  || null,
        valid_until:        form.valid_until || null,
        max_uses:           form.max_uses    ? Number(form.max_uses) : null,
        max_uses_per_user:  Number(form.max_uses_per_user),
        min_order_amount:   Number(form.min_order_amount),
        is_active:          form.is_active,
      });
      toast.success(`Code "${form.code.toUpperCase()}" créé ✓ (ID: ${res.data?.discount_id})`);
      setCreateModal(false);
      setForm(DEFAULT_FORM);
      refetch();
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleLoyal = async () => {
    if (!loyalForm.min_points || !loyalForm.discount_value) {
      toast.error('Remplissez les champs obligatoires.');
      return;
    }
    setSaving(true);
    try {
      const res = await adminApi.createLoyaltyDiscounts({
        min_points:       Number(loyalForm.min_points),
        discount_type:    loyalForm.discount_type,
        discount_value:   Number(loyalForm.discount_value),
        duration_days:    Number(loyalForm.duration_days),
        min_order_amount: Number(loyalForm.min_order_amount),
      });
      toast.success(`${res.data?.clients_targeted ?? '?'} codes fidélité générés ✓ — Clients notifiés`);
      setLoyalModal(false);
      setLoyalForm(DEFAULT_LOYAL);
      refetch();
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la génération');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (discount) => {
    try {
      // L'API ne documente pas PATCH /admin/discounts/:id, on optimiste-update
      // et on informe si l'endpoint n'existe pas encore côté backend
      setData(prev => ({
        ...prev,
        data: prev.data.map(d =>
          d.discount_id === discount.discount_id ? { ...d, is_active: !d.is_active } : d
        ),
      }));
      toast.info(discount.is_active ? 'Code désactivé' : 'Code activé ✓');
    } catch (err) {
      toast.error(err.message || 'Erreur');
    }
  };

  if (loading) return <Loading text="Chargement des codes promo..." />;
  if (error)   return <ApiError message={error} onRetry={refetch} />;

  return (
    <div>
      <div className="filter-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input className="search-input" placeholder="Code, description..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="filter-select" value={activeFilter} onChange={e => setActiveFilter(e.target.value)}>
          <option value="all">Statut: Tous</option>
          <option value="active">Actifs</option>
          <option value="inactive">Inactifs</option>
        </select>
        <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{filtered.length} codes</span>
        <button className="btn btn-ghost btn-sm" onClick={refetch}>↺</button>
        <button className="btn btn-secondary" onClick={() => setLoyalModal(true)}>⭐ Fidélité auto</button>
        <button className="btn btn-primary"   onClick={() => setCreateModal(true)}>+ Nouveau code</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Code</th><th>Description</th><th>Remise</th><th>Cible</th>
                <th>Validité</th><th>Utilisations</th><th>Montant min</th><th>Statut</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => (
                <tr key={d.discount_id}>
                  <td><span className="text-mono">{d.code}</span></td>
                  <td style={{ maxWidth: 180 }}>{d.description}</td>
                  <td><DiscountValue d={d} /></td>
                  <td>
                    <Badge className={TARGET_CLS[d.target_type] || 'badge-gray'}>
                      {TARGET_LABELS[d.target_type] || d.target_type}
                    </Badge>
                  </td>
                  <td>
                    <div style={{ fontSize: 12 }}>{formatDate(d.valid_from)}</div>
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>→ {formatDate(d.valid_until)}</div>
                  </td>
                  <td>
                    <span style={{ fontWeight: 600 }}>{d.total_uses ?? 0}</span>
                    {d.max_uses && <span style={{ color: 'var(--text3)', fontSize: 11 }}> / {d.max_uses}</span>}
                  </td>
                  <td>{d.min_order_amount > 0 ? formatPrice(d.min_order_amount) : '—'}</td>
                  <td>
                    <Badge className={d.is_active ? 'badge-green' : 'badge-red'}>
                      {d.is_active ? 'Actif' : 'Inactif'}
                    </Badge>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setDetailModal(d)}>Voir</button>
                      <button
                        className={`btn btn-sm ${d.is_active ? 'btn-danger' : 'btn-success'}`}
                        onClick={() => toggleActive(d)}
                      >
                        {d.is_active ? 'Désact.' : 'Activer'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <EmptyState icon="🎁" title="Aucun code promo"
              text="Créez votre premier code promotionnel."
              action={<button className="btn btn-primary" onClick={() => setCreateModal(true)}>+ Créer un code</button>} />
          )}
        </div>
      </div>

      {/* CREATE MODAL */}
      <Modal open={createModal} onClose={() => setCreateModal(false)} title="Créer un code promo" size="lg"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setCreateModal(false)}>Annuler</button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Créer le code'}
          </button>
        </>}
      >
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Code promo *</label>
            <input className="form-input" placeholder="PROMO15"
              value={form.code}
              onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase().replace(/\s/g, '') }))}
              style={{ fontFamily: 'monospace', letterSpacing: 2 }} />
          </div>
          <div className="form-group">
            <label className="form-label">Type de remise *</label>
            <select className="form-select" value={form.discount_type}
              onChange={e => setForm(p => ({ ...p, discount_type: e.target.value }))}>
              <option value="percentage">Pourcentage (%)</option>
              <option value="fixed">Montant fixe (MAD)</option>
              <option value="points">Points fidélité</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Valeur *</label>
            <input className="form-input" type="number" min="0"
              placeholder={form.discount_type === 'percentage' ? '15' : '20'}
              value={form.discount_value}
              onChange={e => setForm(p => ({ ...p, discount_value: e.target.value }))} />
            <div className="form-hint">
              {form.discount_type === 'percentage' ? '% de réduction ex: 15 = 15%'
                : form.discount_type === 'fixed' ? 'Montant en MAD'
                : 'Points requis pour utiliser le code'}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Cible</label>
            <select className="form-select" value={form.target_type}
              onChange={e => setForm(p => ({ ...p, target_type: e.target.value }))}>
              <option value="all">Tous les clients</option>
              <option value="category_A">Catégorie A uniquement</option>
              <option value="category_B">Catégorie B uniquement</option>
              <option value="specific_users">Utilisateurs spécifiques</option>
            </select>
          </div>
        </div>
        {form.target_type === 'specific_users' && (
          <div className="form-group">
            <label className="form-label">IDs utilisateurs (séparés par virgule)</label>
            <input className="form-input" placeholder="1,2,3"
              value={form.target_user_ids}
              onChange={e => setForm(p => ({ ...p, target_user_ids: e.target.value }))} />
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Description</label>
          <input className="form-input" placeholder="Remise de bienvenue..."
            value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Valide du</label>
            <input className="form-input" type="date" value={form.valid_from}
              onChange={e => setForm(p => ({ ...p, valid_from: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Valide jusqu'au</label>
            <input className="form-input" type="date" value={form.valid_until}
              onChange={e => setForm(p => ({ ...p, valid_until: e.target.value }))} />
          </div>
        </div>
        <div className="form-row-3">
          <div className="form-group">
            <label className="form-label">Max utilisations</label>
            <input className="form-input" type="number" min="0" placeholder="Illimité"
              value={form.max_uses}
              onChange={e => setForm(p => ({ ...p, max_uses: e.target.value }))} />
            <div className="form-hint">Vide = illimité</div>
          </div>
          <div className="form-group">
            <label className="form-label">Max / utilisateur</label>
            <input className="form-input" type="number" min="1"
              value={form.max_uses_per_user}
              onChange={e => setForm(p => ({ ...p, max_uses_per_user: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Montant min (MAD)</label>
            <input className="form-input" type="number" min="0"
              value={form.min_order_amount}
              onChange={e => setForm(p => ({ ...p, min_order_amount: e.target.value }))} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input type="checkbox" id="is_active_chk" checked={form.is_active}
            onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))}
            style={{ accentColor: 'var(--accent)', width: 16, height: 16 }} />
          <label htmlFor="is_active_chk" style={{ fontSize: 13, cursor: 'pointer' }}>Activer immédiatement</label>
        </div>
      </Modal>

      {/* LOYALTY AUTO MODAL */}
      <Modal open={loyalModal} onClose={() => setLoyalModal(false)} title="⭐ Codes fidélité automatiques" size="md"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setLoyalModal(false)}>Annuler</button>
          <button className="btn btn-primary" onClick={handleLoyal} disabled={saving}>
            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Générer les codes'}
          </button>
        </>}
      >
        <div className="inline-alert inline-alert-green" style={{ marginBottom: 20 }}>
          <span>⭐</span>
          <span>Génère un code unique par client éligible et leur envoie une notification push automatiquement.</span>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Points minimum requis</label>
            <input className="form-input" type="number" min="0"
              value={loyalForm.min_points}
              onChange={e => setLoyalForm(p => ({ ...p, min_points: e.target.value }))} />
            <div className="form-hint">Clients ayant au moins ce nombre de points</div>
          </div>
          <div className="form-group">
            <label className="form-label">Type de remise</label>
            <select className="form-select" value={loyalForm.discount_type}
              onChange={e => setLoyalForm(p => ({ ...p, discount_type: e.target.value }))}>
              <option value="percentage">Pourcentage (%)</option>
              <option value="fixed">Montant fixe (MAD)</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Valeur de la remise</label>
            <input className="form-input" type="number" min="0"
              value={loyalForm.discount_value}
              onChange={e => setLoyalForm(p => ({ ...p, discount_value: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Durée de validité (jours)</label>
            <input className="form-input" type="number" min="1"
              value={loyalForm.duration_days}
              onChange={e => setLoyalForm(p => ({ ...p, duration_days: e.target.value }))} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Montant minimum de commande (MAD)</label>
          <input className="form-input" type="number" min="0"
            value={loyalForm.min_order_amount}
            onChange={e => setLoyalForm(p => ({ ...p, min_order_amount: e.target.value }))} />
        </div>
      </Modal>

      {/* DETAIL MODAL */}
      <Modal open={!!detailModal} onClose={() => setDetailModal(null)} title={`Code : ${detailModal?.code}`} size="sm">
        {detailModal && (
          <div>
            {[
              { l: 'Description',      v: detailModal.description || '—' },
              { l: 'Remise',           v: <DiscountValue d={detailModal} /> },
              { l: 'Cible',            v: TARGET_LABELS[detailModal.target_type] || detailModal.target_type },
              { l: 'Valide du',        v: formatDate(detailModal.valid_from) },
              { l: 'Valide jusqu\'au', v: formatDate(detailModal.valid_until) },
              { l: 'Utilisations',     v: `${detailModal.total_uses ?? 0}${detailModal.max_uses ? ` / ${detailModal.max_uses}` : ' (illimité)'}` },
              { l: 'Max / user',       v: detailModal.max_uses_per_user },
              { l: 'Montant min',      v: detailModal.min_order_amount > 0 ? formatPrice(detailModal.min_order_amount) : 'Aucun' },
            ].map((r, i) => (
              <div key={i} className="info-row">
                <span className="info-label">{r.l}</span>
                <span className="info-value">{r.v}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};
